import '../styles/index.css'
import * as THREE from 'three/webgpu'
import { Fn, vec3, float, positionWorld, time, texture } from 'three/tsl'
import { bloom } from 'three/examples/jsm/tsl/display/BloomNode.js'
import { pixelationPass } from 'three/examples/jsm/tsl/display/PixelationPassNode.js'
import { rgbShift } from 'three/examples/jsm/tsl/display/RGBShiftNode.js'

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js'

import World from './sim/World'
import FloorTitle from './scene/FloorTitle'
import { initStatusRail, updateRail } from './ui/statusRail'

// TSL pixelation settings — block size + edge strengths
const PIXEL_SIZE = 4
const NORMAL_EDGE = 0.3
const DEPTH_EDGE = 0.4

// Cyberpunk grid-floor TSL material: cyan grid, amber center glow,
// scan-sweep ring, radial fade so edges melt into fog.
function makeGridFloorMaterial() {
  const mat = new THREE.MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  })

  mat.colorNode = Fn(() => {
    const pos = positionWorld.xz
    const cell = float(8)

    // Distance from each cell centerline — min of X and Z gives grid lines
    const g = pos.div(cell).fract().sub(0.5).abs()
    const lineDist = g.x.min(g.y)
    const gridMask = lineDist.smoothstep(0.0, 0.045).oneMinus()

    // Major rings every 8 cells — thicker amber accents
    const majorStep = pos.div(cell.mul(8)).fract().sub(0.5).abs()
    const majorDist = majorStep.x.min(majorStep.y)
    const majorMask = majorDist.smoothstep(0.0, 0.012).oneMinus()

    const r = pos.length()

    // Radial fade — grid strong near origin, melts out by ~260
    const fade = r.smoothstep(40, 260).oneMinus()
    const centerGlow = r.smoothstep(0, 90).oneMinus().mul(0.18)

    // Scan sweep — expanding ring, animated
    const scanPhase = r.mul(0.006).sub(time.mul(0.18)).fract()
    const scan = scanPhase.smoothstep(0.92, 1.0).mul(fade).mul(0.6)

    const cyan = vec3(0.49, 0.96, 0.84)
    const amber = vec3(1.0, 0.48, 0.1)
    const hotAmber = vec3(1.0, 0.65, 0.25)

    const gridLayer = cyan.mul(gridMask).mul(fade).mul(0.7)
    const majorLayer = amber.mul(majorMask).mul(fade).mul(0.35)
    const scanLayer = hotAmber.mul(scan)
    const centerLayer = amber.mul(centerGlow)

    return gridLayer.add(majorLayer).add(scanLayer).add(centerLayer)
  })()

  mat.opacityNode = Fn(() => {
    const r = positionWorld.xz.length()
    return r.smoothstep(60, 420).oneMinus()
  })()

  return mat
}

export default class Demo {
  constructor() {
    this.parameters = { thirdPersonCamera: false }
    this.init()
  }

  async init() {
    this.time = new THREE.Clock()
    this.previousTime = 0
    this.mixers = []
    this.controls = []
    this.currentAction = null

    this.container = document.getElementById('webgl-container')
    this.width = this.container.offsetWidth
    this.height = this.container.offsetHeight

    // Renderer — WebGPU with WebGL2 fallback
    this.renderer = new THREE.WebGPURenderer({ antialias: false })
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.toneMapping = THREE.ReinhardToneMapping
    this.renderer.toneMappingExposure = 2.3
    this.renderer.outputColorSpace = THREE.SRGBColorSpace

    try {
      await this.renderer.init()
    } catch (err) {
      console.warn('[cwk] WebGPU unavailable, falling back:', err)
    }

    this.isWebGPU = !!this.renderer.backend?.isWebGPUBackend
    initStatusRail()

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(this.width, this.height)
    this.container.appendChild(this.renderer.domElement)
    this.renderer.domElement.style.width = '100%'
    this.renderer.domElement.style.height = '100%'
    this.renderer.domElement.style.display = 'block'

    // Camera
    const fov = 30
    const aspect = this.width / this.height
    this.camera = new THREE.PerspectiveCamera(fov, aspect, 1, 2000)
    this.camera.position.set(0, 50, 100)

    // Scene
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0x0a0a08)
    this.scene.fog = new THREE.FogExp2(0x0a0a08, 0.0045)

    // TSL Post-Processing — pixelation → bloom → rgbShift
    this.postProcessing = new THREE.PostProcessing(this.renderer)
    const pixelPass = pixelationPass(
      this.scene,
      this.camera,
      PIXEL_SIZE,
      NORMAL_EDGE,
      DEPTH_EDGE
    )
    const bloomPass = bloom(pixelPass, 0.55, 0.35, 0.82)
    const combined = pixelPass.add(bloomPass)
    const shifted = rgbShift(combined, 0.0022, Math.PI / 4)
    this.postProcessing.outputNode = shifted

    // Lights — warm amber key, cool fill
    const key = new THREE.SpotLight(0xffa95c, 8)
    key.position.set(-50, 550, 50)
    key.angle = Math.PI / 5
    key.penumbra = 0.4
    key.castShadow = true
    key.shadow.bias = -0.0001
    key.shadow.mapSize.set(2048, 2048)
    this.scene.add(key)

    const hemi = new THREE.HemisphereLight(0xffd9a0, 0x0a1a2a, 2.6)
    this.scene.add(hemi)

    const rim = new THREE.DirectionalLight(0x7cf5d7, 0.6)
    rim.position.set(50, 30, -40)
    this.scene.add(rim)

    // Locked orbit controls (identical feel to before)
    this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement)
    this.orbitControls.enableDamping = true
    this.orbitControls.target.set(0, 20, 0)
    this.orbitControls.enableZoom = false
    this.orbitControls.enablePan = false
    this.orbitControls.enableRotate = false
    this.orbitControls.update()

    // Invisible floor used for raycasting the cat's target position
    const visibleHeight = 2 * Math.tan((fov * Math.PI) / 180 / 2) * 100
    const visibleWidth = visibleHeight * aspect
    const planeGeometry = new THREE.PlaneGeometry(
      visibleWidth * 20,
      visibleHeight * 100,
      10,
      10
    )
    this.planeGeometry = planeGeometry

    const planeMaterial = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.95,
      roughness: 0.05,
      transparent: true,
      opacity: 0,
    })
    const plane = new THREE.Mesh(planeGeometry, planeMaterial)
    plane.receiveShadow = true
    plane.rotation.x = -Math.PI / 2
    plane.name = 'Floor Plane'
    this.scene.add(plane)
    this.plane = plane

    // Cyberpunk grid floor — sits just beneath the invisible raycast plane
    const gridFloor = new THREE.Mesh(
      new THREE.PlaneGeometry(900, 900, 1, 1),
      makeGridFloorMaterial()
    )
    gridFloor.rotation.x = -Math.PI / 2
    gridFloor.position.y = -0.05
    gridFloor.renderOrder = -1
    this.scene.add(gridFloor)
    this.gridFloor = gridFloor

    // Hero title painted on the floor — makes the brand part of the scene
    this.floorTitle = new FloorTitle({ scene: this.scene, camera: this.camera })

    // FPS tracking
    this._fpsFrames = 0
    this._fpsLast = performance.now()
    this._fps = 0

    // World — owns cats, ball, reproduction, persistence
    this.world = new World({
      scene: this.scene,
      camera: this.camera,
      modelPath: '/models/fatcat/',
    })

    // Raycast-based click: hits cat → pet; hits ground → move ball
    this._clickRaycaster = new THREE.Raycaster()
    this._ndc = new THREE.Vector2()

    const isInteractive = (el) =>
      el && el.closest('input, textarea, button, a, label, .contact, .rail, .footer')

    const handlePick = (clientX, clientY) => {
      this._ndc.x = (clientX / window.innerWidth) * 2 - 1
      this._ndc.y = -(clientY / window.innerHeight) * 2 + 1
      this._clickRaycaster.setFromCamera(this._ndc, this.camera)

      // Hit a cat? Each cat's `target` object + its descendants represent the cat.
      const catHits = []
      for (const cat of this.world.cats) {
        if (!cat.target) continue
        const hits = this._clickRaycaster.intersectObject(cat.target, true)
        if (hits.length > 0) catHits.push({ cat, distance: hits[0].distance })
      }
      if (catHits.length > 0) {
        catHits.sort((a, b) => a.distance - b.distance)
        this.world.petCat(catHits[0].cat)
        return
      }

      // Otherwise hit the invisible floor → move the ball
      const floorHit = this._clickRaycaster.intersectObject(this.plane)
      if (floorHit.length > 0) {
        this.world.moveBall(floorHit[0].point)
      }
    }

    window.addEventListener('click', (e) => {
      if (isInteractive(e.target)) return
      handlePick(e.clientX, e.clientY)
    })
    window.addEventListener('touchstart', (e) => {
      if (isInteractive(e.target)) return
      const t = e.touches[0]
      if (t) handlePick(t.clientX, t.clientY)
    }, { passive: true })

    // Manual evolve trigger
    const evolveBtn = document.getElementById('evolveBtn')
    if (evolveBtn) evolveBtn.addEventListener('click', () => this.world.evolve())

    this.setupResize()
    this.render()
  }

  resize() {
    this.width = this.container.offsetWidth
    this.height = this.container.offsetHeight
    this.camera.aspect = this.width / this.height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(this.width, this.height)
  }

  setupResize() {
    window.addEventListener('resize', this.resize.bind(this), false)
  }

  _tickFPS(now) {
    this._fpsFrames++
    if (now - this._fpsLast >= 500) {
      this._fps = Math.round((this._fpsFrames * 1000) / (now - this._fpsLast))
      this._fpsFrames = 0
      this._fpsLast = now
    }
  }

  async render() {
    const elapsed = this.time.getElapsedTime()
    const delta = Math.min(0.1, elapsed - this.previousTime) // clamp for tab blurs
    this.previousTime = elapsed

    this.world.update(delta)

    await this.postProcessing.renderAsync()

    const now = performance.now()
    this._tickFPS(now)
    const stats = this.world.getStats()
    updateRail({
      cats: stats.count,
      fps: this._fps,
      generation: stats.generation,
    })

    window.requestAnimationFrame(this.render.bind(this))
  }
}

window.addEventListener('DOMContentLoaded', () => {
  const demo = new Demo()
  window.demo = demo
})

// Scroll-to-contact helpers
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-scroll-to]')
  if (!trigger) return
  const id = trigger.getAttribute('data-scroll-to')
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
})
